import React, { useState, useEffect, useCallback } from 'react';
import { Table, Container, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const ReportsList = ({ 
  gitlabUrlAutomation, 
  PAT, 
  reportDate, 
  selectedDate, 
  filteredPipelines, 
  setFilteredPipelines, 
  setReportLoading,
  reportRefreshTrigger,
  reportLoading
}) => {
  const [error, setError] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Get downstream pipeline information (QA pipeline)
  const fetchPipelineDownstream = useCallback(async (pipelineId) => {
    try {
      const response = await fetch(`${gitlabUrlAutomation}/pipelines/${pipelineId}/bridges?downstream_pipeline=true`, {
        headers: {
          'PRIVATE-TOKEN': PAT,
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0 && data[0].downstream_pipeline) {
          return data[0].downstream_pipeline;
        }
      }
      return null;
    } catch (error) {
      console.error(`Error fetching downstream pipeline for ${pipelineId}:`, error);
      return null;
    }
  }, [gitlabUrlAutomation, PAT]);

  // Get jobs from a pipeline to extract groups/stages
  const fetchPipelineJobs = useCallback(async (pipelineId) => {
    try {
      const response = await fetch(`${gitlabUrlAutomation}/pipelines/${pipelineId}/jobs?per_page=100`, {
        headers: {
          'PRIVATE-TOKEN': PAT,
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const jobs = await response.json();
        // Extract unique stages/groups from jobs
        const groups = [...new Set(jobs.map(job => job.stage))];
        return groups;
      }
      return [];
    } catch (error) {
      console.error(`Error fetching jobs for pipeline ${pipelineId}:`, error);
      return [];
    }
  }, [gitlabUrlAutomation, PAT]);

  // Fetch variables for fallback
  const fetchPipelineVariables = useCallback(async (pipelineId) => {
    try {
      const response = await fetch(`${gitlabUrlAutomation}/pipelines/${pipelineId}/variables`, {
        headers: {
          'PRIVATE-TOKEN': PAT,
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Extract CI_GROUP variable
        const ciGroup = data.find(v => v.key === 'CI_GROUP');
        return ciGroup ? ciGroup.value : null;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching variables for pipeline ${pipelineId}:`, error);
      return null;
    }
  }, [gitlabUrlAutomation, PAT]);

  // Fetch all pipeline details
  const fetchPipelineDetails = useCallback(async (pipelineIds) => {
    if (!pipelineIds || pipelineIds.length === 0) return {};
    
    setLoadingDetails(true);
    const details = {};
    
    try {
      // Process in smaller batches to avoid overloading the API
      const batchSize = 3; // Smaller batch size due to more complex operations
      for (let i = 0; i < pipelineIds.length; i += batchSize) {
        const batchIds = pipelineIds.slice(i, i + batchSize);
        
        await Promise.all(batchIds.map(async (id) => {
          try {
            // Step 1: Get downstream pipeline
            const downstream = await fetchPipelineDownstream(id);
            
            if (downstream) {
              // Step 2: If downstream exists, get its job groups
              const groups = await fetchPipelineJobs(downstream.id);
              
              details[id] = {
                qaId: downstream.id,
                qaStatus: downstream.status,
                groups: groups,
                groupsString: groups.join(', ')
              };
            } else {
              // Fallback to variables if no downstream
              const ciGroup = await fetchPipelineVariables(id);
              details[id] = {
                qaId: null,
                qaStatus: null,
                groups: ciGroup ? [ciGroup] : [],
                groupsString: ciGroup || 'N/A'
              };
            }
          } catch (error) {
            console.error(`Error processing details for pipeline ${id}:`, error);
            details[id] = {
              qaId: null,
              qaStatus: null,
              groups: [],
              groupsString: 'Error'
            };
          }
        }));
      }
      
      return details;
    } catch (error) {
      console.error('Error fetching pipeline details:', error);
      return {};
    } finally {
      setLoadingDetails(false);
    }
  }, [fetchPipelineDownstream, fetchPipelineJobs, fetchPipelineVariables]);

  const fetchScheduledPipelines = useCallback(async (targetDate = null) => {
    setReportLoading(true);
    setError(null);

    // If no specific date provided, use the selected date from props
    const dateToUse = targetDate || moment(selectedDate);
    const dateFormatted = dateToUse.format('YYYY-MM-DD');
    
    try {
      // Build query params for scheduled pipelines - get more to filter by date later
      const queryParams = new URLSearchParams();
      queryParams.append('per_page', 100);
      queryParams.append('source', 'schedule');

      const response = await fetch(`${gitlabUrlAutomation}/pipelines?${queryParams.toString()}`, {
        headers: {
          'PRIVATE-TOKEN': PAT,
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch scheduled pipelines');
      }

      const data = await response.json();
      
      // Filter pipelines from selected day only
      const dayPipelines = data.filter(pipeline => {
        const pipelineDate = moment(pipeline.created_at).format('YYYY-MM-DD');
        return pipelineDate === dateFormatted;
      });
      
      console.log(`Found ${dayPipelines.length} pipelines from ${dateFormatted}`);
      
      // Set pipelines immediately for better UX
      setFilteredPipelines(dayPipelines);
      
      // Fetch detailed information for the filtered pipelines
      if (dayPipelines.length > 0) {
        const details = await fetchPipelineDetails(dayPipelines.map(p => p.id));
        
        // Update pipeline objects with group information
        const updatedPipelines = dayPipelines.map(pipeline => ({
          ...pipeline,
          qaDetails: details[pipeline.id] || {
            qaId: null,
            qaStatus: null,
            groups: [],
            groupsString: 'N/A'
          }
        }));
        
        // Filter out pipelines with "visual-regression" in their groups
        const filteredUpdatedPipelines = updatedPipelines.filter(pipeline => {
          const groups = pipeline.qaDetails?.groups || [];
          return !groups.some(group => group.includes('visual-regression'));
        });
        
        console.log(`Filtered out ${updatedPipelines.length - filteredUpdatedPipelines.length} visual regression pipelines`);
        setFilteredPipelines(filteredUpdatedPipelines);
      } else {
        setFilteredPipelines([]);
      }
    } catch (error) {
      console.error('Error fetching scheduled pipelines:', error);
      setError(error.message);
    } finally {
      setReportLoading(false);
    }
  }, [gitlabUrlAutomation, PAT, selectedDate, setFilteredPipelines, setReportLoading, fetchPipelineDetails]);

  useEffect(() => {
    fetchScheduledPipelines();
  }, [fetchScheduledPipelines, reportRefreshTrigger]);

  // Get the main group from a group string (usually first word or hyphenated term)
  const extractMainGroup = (groupName) => {
    if (!groupName) return null;
    
    // Check for hyphenated terms like "e2e-tests" or "api-integration"
    const hyphenatedMatch = groupName.match(/^([a-z0-9]+-[a-z0-9]+)/i);
    if (hyphenatedMatch) {
      return hyphenatedMatch[0].toLowerCase();
    }
    
    // Otherwise just get the first word
    const firstWord = groupName.split(/[^a-z0-9-]/i)[0].toLowerCase();
    return firstWord || groupName.toLowerCase();
  };
  
  // Group pipelines by their main group category
  const groupPipelineGroups = (groups) => {
    if (!groups || groups.length === 0) return [];
    
    // Extract main groups and remove duplicates
    const mainGroups = [...new Set(groups.map(extractMainGroup).filter(Boolean))];
    return mainGroups;
  };

  const getStatusBadge = (status) => {
    let variant;
    let icon;

    switch (status) {
      case 'success':
        variant = 'success';
        icon = ['far', 'circle-check'];
        break;
      case 'failed':
        variant = 'danger';
        icon = ['fas', 'circle-exclamation'];
        break;
      case 'running':
        variant = 'primary';
        icon = ['fas', 'circle-play'];
        break;
      case 'pending':
        variant = 'warning';
        icon = ['fas', 'hourglass-half'];
        break;
      case 'canceled':
        variant = 'secondary';
        icon = ['fas', 'ban'];
        break;
      default:
        variant = 'info';
        icon = ['fas', 'circle-dot'];
    }

    return (
      <Badge bg={variant} className="d-flex align-items-center gap-1">
        <FontAwesomeIcon icon={icon} size="sm" />
        <span>{status}</span>
      </Badge>
    );
  };

  const getGroupBadge = (group) => {
    if (!group) return <Badge bg="secondary">N/A</Badge>;
    
    // Assign colors based on group name
    let variant;
    let icon = ['fas', 'layer-group'];
    
    // Determine color based on group name
    if (group.includes('smoke')) {
      variant = 'info';
    } else if (group.includes('regression')) {
      variant = 'danger';
    } else if (group.includes('e2e')) {
      variant = 'success';
    } else if (group.includes('api')) {
      variant = 'warning';
    } else if (group.includes('ui')) {
      variant = 'primary';
    } else if (group.includes('perf')) {
      variant = 'dark';
    } else if (group.includes('integration')) {
      variant = 'secondary';
    } else if (group.includes('unit')) {
      variant = 'light';
      icon = ['fas', 'cube'];
    } else {
      variant = 'secondary';
    }
    
    // Convert group name to a more readable format
    const displayName = group
      .replace(/-/g, ' ')
      .replace(/^(.)/, match => match.toUpperCase());
    
    return (
      <Badge bg={variant} className="d-flex align-items-center gap-1 text-capitalize">
        <FontAwesomeIcon icon={icon} size="sm" />
        <span>{displayName}</span>
      </Badge>
    );
  };

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  // Table skeleton for loading state
  const TableSkeleton = () => (
    <div className="table-responsive reports-table-container" style={{ maxHeight: 'calc(70vh - 30px)', overflowY: 'auto' }}>
      <Table striped hover>
        <thead className="sticky-top bg-light">
          <tr>
            <th>ID</th>
            <th>Status</th>
            <th>Groups</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {[...Array(8)].map((_, index) => (
            <tr key={index}>
              <td><Skeleton width={60} /></td>
              <td>
                <Skeleton width={80} height={24} borderRadius={20} />
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  {[...Array(Math.floor(Math.random() * 3) + 1)].map((_, i) => (
                    <Skeleton key={i} width={60} height={24} borderRadius={20} />
                  ))}
                </div>
              </td>
              <td><Skeleton width={100} /></td>
              <td>
                <Skeleton width={40} height={30} borderRadius={4} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2 className="mb-4">Scheduled Pipeline Reports</h2>
          
          {reportLoading ? (
            <TableSkeleton />
          ) : filteredPipelines.length > 0 ? (
            <div className="table-responsive reports-table-container" style={{ maxHeight: 'calc(70vh - 30px)', overflowY: 'auto' }}>
              <Table striped hover>
                <thead className="sticky-top bg-light">
                  <tr>
                    <th>ID</th>
                    <th>Status</th>
                    <th>Groups</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPipelines.map(pipeline => (
                    <tr key={pipeline.id}>
                      <td>#{pipeline.id}</td>
                      <td>
                        {pipeline.qaDetails?.qaStatus ? (
                          getStatusBadge(pipeline.qaDetails.qaStatus)
                        ) : (
                          <Badge bg="light" text="dark">N/A</Badge>
                        )}
                      </td>
                      <td>
                        {loadingDetails ? (
                          <Spinner animation="border" size="sm" />
                        ) : pipeline.qaDetails?.groups?.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1" title={pipeline.qaDetails.groups.join(', ')}>
                            {groupPipelineGroups(pipeline.qaDetails.groups).map((group, index) => (
                              <span key={index}>{getGroupBadge(group)}</span>
                            ))}
                          </div>
                        ) : (
                          <Badge bg="secondary">N/A</Badge>
                        )}
                      </td>
                      <td>{moment(pipeline.created_at).format('MMM DD, HH:mm')}</td>
                      <td>
                        {pipeline.qaDetails?.qaId ? (
                        <a 
                            href={`https://gitlab.devpayever.com/qa/automation/-/pipelines/${pipeline.qaDetails.qaId}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-info"
                            title="View QA pipeline"
                        >
                            <FontAwesomeIcon icon={['fas', 'vial']} size="sm" className="me-1" />
                            <FontAwesomeIcon icon={['fas', 'arrow-up-right-from-square']} size="xs" />
                        </a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="info">No pipelines found for the selected date.</Alert>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ReportsList; 